import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { capitalizeFirstLetter } from "../../utils";
import { LabelObject } from "../../interfaces";
import { AppContext } from "../../context/index";
import { enabledMaster, locationMaster, functionMaster, REVALIDATE_FORM_PROPS } from "../../constants";
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Button, Calendar, InputText } from "iuvity-ui-components";
import { Tag } from "iuvity-ui-components/dist/tag";
import { FilterDialog } from "../../components/environments";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";
import { PrivilegeValidatorComponent } from "../shared/PrivilegeValidatorComponent";

interface UserAdminProps {
  data: any;
  userAction: (action: any, data: any) => Promise<string>;
  reloadFn: () => void;
  isDeleteComponent?: boolean;
  enableOtp?: boolean;
}

const formBlankValues = {
  firstName: '',
  lastName: '',
  email: '',
  status: '',
  lastAccessDate: '',
  function: '',
  location: '',
  createdAt: '',
  deletedAt: '',
};

export const UserAdminComponent: React.FC<UserAdminProps> = ({
  isDeleteComponent = false,
  data,
  userAction,
  reloadFn,
  enableOtp
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const formKeysOptions = {
    status: enabledMaster,
    location: locationMaster,
    function: functionMaster,
  };

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
        firstName: pageFilter?.firstName || '',
        lastName: pageFilter?.lastName || '',
        email: pageFilter?.email || '',
        status: pageFilter?.status || '',
        lastAccessDate: pageFilter?.lastAccessDate || '',
        function: pageFilter?.function || '',
        location: pageFilter?.location || '',
        createdAt: pageFilter?.createdAt || '',
        deletedAt: pageFilter?.deletedAt || '',
      }
      : formBlankValues,
  });

  const { control, setValue } = form;

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    firstName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    lastName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.IN },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastAccessDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    function: { value: null, matchMode: FilterMatchMode.IN },
    location: { value: null, matchMode: FilterMatchMode.IN },
    createdAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
    deletedAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
  }

  const [filters, setFilters] = useState(defaultFilterValues);
  const [filteredData, setFilteredData] = useState<any | null>(null);
  const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);
  const [lang, setLang] = useState<string>(localStorage.getItem('LANGUAGE')?.slice(0, 2).toLowerCase() || 'es')
  const [columns, setColumns] = useState<any>([])
  const [selectedColumns, setSelectedColumns] = useState<any>([]);
  const [selectRows, setSelectRows] = useState([]);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  useEffect(() => {
    const handleCallCloseDialogEvent = ((
      event: CustomEvent<{ detail: string }>
    ) => {
      setLang(event.detail.toString())
      setColumns([])
      setSelectedColumns([])
      // eslint-disable-next-line no-undef
    }) as EventListener
    window.addEventListener('CHANGE::LANGUAGE', handleCallCloseDialogEvent)
    return () => window.removeEventListener('CHANGE::LANGUAGE', handleCallCloseDialogEvent)
  }, []);

  useEffect(() => {
    const columsDefault = [
      {
        field: "select",
        header: "",
      },
      {
        field: "firstName",
        header: t("Users.firstNameLabel"),
      },
      {
        field: "lastName",
        header: t("Users.lastNameLabel"),
      },
      {
        field: "email",
        header: t("Users.emailLabel"),
      },
      {
        field: "status",
        header: t("Users.statusLabel"),
      },
      {
        field: "lastAccessDate",
        header: t("Users.lastAccessDateLabel"),
      },
      {
        field: "function",
        header: t("Users.functionLabel"),
      },
      {
        field: "location",
        header: t("Users.locationLabel"),
      },
      {
        field: "createdAt",
        header: t("Users.createdAtLabel"),
      },
      {
        field: "deletedAt",
        header: t("Users.deletedAtLabel"),
      },
      {
        field: "actions",
        header: t("Users.actionsLabel"),
      },
    ]
    const res = isDeleteComponent
      ? columsDefault.filter((obj: any) => obj.field !== 'createdAt' && obj.field !== 'select')
      : columsDefault.filter((obj: any) => obj.field !== 'deletedAt')
    setColumns(res)
    setSelectedColumns(res)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const formOptions = useMemo(() => {
    if (data) {
      return {
        firstName: {},
        lastName: {},
        email: {},
        location: formKeysOptions.location(t),
        function: formKeysOptions.function(t),
        status: formKeysOptions.status(t),
      }
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t, lang]);

  const actionDetailFn = (label: any) => {
    setAppState((prev) => ({
      ...prev,
      selectedUser: label,
      selectedApplicationRoles: []
    }));
    navigate(`${location.pathname}/detail`);
    window.scrollTo(0, 0);
  };

  const actionEditFn = (label: LabelObject) => {
    setAppState((prev) => ({
      ...prev,
      selectedUser: label,
      selectedApplicationRoles: [],
      selectedManagementRoles: [],
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (usersData: any) => {
    const userIds = usersData.map((item: any) => item.id).join();
    await userAction("DELETEALL", userIds);
    reloadFn();
    setSelectRows([]);
  };

  const actionSendTempPassFn = async (user: any) => {
    await userAction("TEMP_PASSWORD", user);
    reloadFn();
  };

  const actionResetOtpFn = async (user: any) => {
    await userAction("RESET_OTP", user);
    reloadFn();
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const handleDeleteModal = async (usersData: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Users.deleteModalTitle"),
      content: `${t("Users.deleteModalContent1")} "${usersData.length}" ${t("Users.deleteModalContent2")}`,
      onConfirm: () => actionDeleteFn(usersData),
    }));
  };

  const handleSendTempPassword = async (user: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Users.sendTemPassModalTitle"),
      content: t("Users.sendTemPassModalContent").replace('{name}', `${user?.firstName} ${user?.lastName}`),
      onConfirm: () => actionSendTempPassFn(user),
    }));
  };
  const handleResetOtp = async (user: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Users.resetOtpModalTitle"),
      content: t("Users.resetOtpModalContent").replace('{name}', `${user?.firstName} ${user?.lastName}`),
      onConfirm: () => actionResetOtpFn(user),
    }));
  };

  const handleSelectRows = (usersSelection: any) => {
    if (usersSelection !== null) {
      setSelectRows(usersSelection);
    }
  };

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo', 'activa'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'deleted', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  const filterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.target.value);
            options.filterCallback(e.target.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-2">
                {t(`Users.${options.field}Label`)}
              </label>
              <InputText
                placeholder={t(`Users.${options.field}PlaceholderLabel`)}
                autoComplete="off"
                type={'text'}
                className="mb-2"
                {...field}
              />
            </>
          );
        }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.filter((user: any) => isDeleteComponent ? user.status === 'DELETED' : user.status !== 'DELETED')
      ?.map((user: any) =>
        ['function', 'location'].includes(options.field)
          ? ({
            label: user?.attributes[`${options.field}`] ? user?.attributes[`${options.field}`] : false,
            value: user?.attributes[`${options.field}`] ? user?.attributes[`${options.field}`] : false
          })
          : ({ label: user[`${options.field}`], value: user[`${options.field}`] }))
      ?.filter((item: { label: string, value: string }) => item.label)
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-1">
                {t(`Users.${options.field}Label`)}
              </label>
              <MultiSelect
                display="chip"
                options={arrayWithoutDuplicates}
                itemTemplate={ItemsTemplate}
                placeholder={t(`Users.${options.field}PlaceholderLabel`)}
                className="mb-2"
                emptyFilterMessage={t("Tenants.noAvailableMessage")}
                filter
                {...field}
              />
            </>
          );
        }}
      />
    )
  };

  const statuses = [
    `${t("Users.activeLabel")}`,
    `${t("Users.inactiveLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Users.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Permissions.${options.field}PlaceholderLabel`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  };

  const formatDate = (value: any) => {
    return new Date(value).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const dateBodyTemplate = (rowData: any) => {
    return rowData ? formatDate(rowData) : null;
  };
  const dateFilterTemplate = (options: any) => {
    // setModifiedatValues(options.value);
    return (
      <Calendar
        label={t(`Users.${options.field}Label`)}
        value={options.value}
        onChange={(e) => e.value ? options.filterCallback(e.value, options.index) : null}
        dateFormat="mm/dd/yy"
        placeholder="mm/dd/yyyy"
        mask="99/99/9999"
        className="mt-2 mb-2"
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFiltersColumn}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        onClick={() => {
          options.filterApplyCallback();
          setAppState((prev: any) => ({
            ...prev,
            // pageFilter: getValues(),
          }));
        }}
      />
    )
  };

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data]);

  return (
    <>
      {!isDeleteComponent &&
        <SubheaderActions
          className="multiactions-c pb-3"
          actions={subheaderActionsPrivilegeValidator([
            {
              id: "111",
              icon: "iu iu-icon-plus",
              label: t("Users.newUserText"),
              style: {
                display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
              },
              onClick: () => {
                setAppState((prev) => ({
                  ...prev,
                  selectedUserRoleInfo: null,
                  listInfoTenants: null,
                  selectedApplicationRoles: [],
                  selectedManagementRoles: []
                }))
                navigate(`${location.pathname}/new`)
              },
              disabled: !data,
            },
          ])}
        />
      }

      <BoxFull className={`dt-c ${isDeleteComponent ? 'my-5' : 'mt-0 mb-5'}`}>
        {filteredData ? (
          <DataTable
            header={
              <TableHeader
                title={t("Users.internalUsersTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
                hasCheckColumn
              />
            }
            currentPageReportTemplate={t('Users.rowsPaginatorTemplateLabel')}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("Users.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="fullname"
            sortOrder={1}
            filters={filters}
            value={filteredData
              ?.filter((item: any) => isDeleteComponent ? item?.status === 'DELETED' : item?.status !== 'DELETED')
              .sort((userA: any, userB: any) => isDeleteComponent ? new Date(userB.deletedAt).getTime() - new Date(userA.deletedAt).getTime() : new Date(userB.createdAt).getTime() - new Date(userA.createdAt).getTime())
              .map((label: any) => (
                {
                  ...label,
                  email: label?.email,
                  status: t(`Users.${label?.status.toLowerCase()}Label`),
                  function: label?.attributes.function,
                  location: label?.attributes.location,
                  lastAccessDate: !!label?.lastAccessDate ? formatStringToDate2(label?.lastAccessDate, true, t) : null,
                  createdAt: !!label?.createdAt ? formatStringToDate2(label?.createdAt, true, t) : null,
                  deletedAt: !!label?.deletedAt ? formatStringToDate2(label?.deletedAt, true, t) : null,
                  actions: !isDeleteComponent
                    ?
                    (
                      <Actions
                        onDetail={() => actionDetailFn(label)}
                        onEdit={() => actionEditFn(label)}
                        viewButtonDelete={false}
                        colorEdit="var(--warningColor)"
                        visibleObject={{ ...permissiondInfoByRole }}
                        auxButtons={[
                          {
                            showButton: permissiondInfoByRole.CREATE && permissiondInfoByRole.UPDATE,
                            label: t('Users.tempPasswordButton'),
                            color: label.availableResetPassword ? 'var(--infoColor)' : 'var(--infoColorTint60)',
                            function: () => handleSendTempPassword(label),
                            icon: "iu-icon-key",
                            disabled: !label.availableResetPassword
                          },
                          {
                            showButton: enableOtp && permissiondInfoByRole.CREATE && permissiondInfoByRole.UPDATE,
                            label: t('Users.resetOtpButton'),
                            color: label.availableResetOtp ? 'var(--infoColor)' : 'var(--infoColorTint60)',
                            function: () => handleResetOtp(label),
                            icon: "iu-icon-password",
                            disabled: !label.availableResetOtp
                          }
                        ]}
                      />
                    )
                    :
                    (
                      <Actions
                        onDetail={() => actionDetailFn(label)}
                        viewButtonDelete={false}
                        viewButtonEdit={false}
                      />
                    ),
                }
              )
              )}
            scrollable
            resizableColumns
            globalFilterFields={[
              "firstName",
              "lastName",
              "email",
              "status",
              "lastAccessDate",
              "function",
              "location",
              "createdAt",
              "deletedAt",
            ]}
            filterDisplay="menu"
            // stateStorage="session"
            // stateKey="filters-table"
            selection={selectRows}
            onSelectionChange={e => handleSelectRows(e.value)}
            rowHover
          >
            {selectedColumns.map((col: any) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                headerStyle={col.field === "actions"
                  ? { justifyContent: "center" }
                  : undefined}
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: col.field === "select" ? "3.5rem" : (col.field === "actions") ? "8.3rem" : "14rem"
                }}
                sortable={!(col.field === "actions" || col.field === "select")}
                align={col.field === "actions" ? "center" : "left"}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filter={(col.field === "actions" || col.field === "select") ? false : true}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: "23rem" }}
                filterElement={
                  ((col.field === "email" || col.field === "function" || col.field === "location") && multiselectFilterTemplate) ||
                  ((col.field === "firstName" || col.field === "lastName") && filterTemplate) ||
                  (col.field === "status" && statusFilterTemplate) ||
                  ((col.field === "lastAccessDate" || col.field === "createdAt" || col.field === "deletedAt") && dateFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                selectionMode={col.field === "select" ? 'multiple' : undefined}
                dataType={(col.field === "lastAccessDate" || col.field === "createdAt" || col.field === "deletedAt") ? "date" : ""}
                body={(rowData) => col.field === "status"
                  ? statusBodyTemplate(rowData)
                  : (col.field === "lastAccessDate" || col.field === "createdAt" || col.field === "deletedAt")
                    ? dateBodyTemplate(rowData[col.field])
                    : rowData[col.field]}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {!isDeleteComponent &&
        <BoxFull className="py-0 px-0 mb-3" style={{ backgroundColor: "transparent" }}>
          <div className="flex align-items-center justify-content-end gap-5">
            <PrivilegeValidatorComponent showButton={permissiondInfoByRole.REMOVE} >
              <Button
                variant="p-button"
                label={t("Users.deleteSelectedLabel")}
                disabled={selectRows.length > 0 ? false : true}
                onClick={() => handleDeleteModal(selectRows)}
              />
            </PrivilegeValidatorComponent>
          </div>
        </BoxFull>
      }

      <FilterDialog
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        data={data}
        setData={setFilteredData}
        onClearFilters={onClearFiltersColumn}
        form={form}
        formOptions={formOptions}
      />

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
